import { z } from "zod";
import { compareVersions } from "@shared/contracts";
import { env, type AppEnv } from "@/app/config/env";
import { getManifest, getManifestStatus, primeManifestFromCache } from "@/app/config/manifest";
import { updates, type ShellIdentity } from "@/app/platform";
import { log, reportMessage } from "@/app/observability";

/**
 * SHIPPING A FIX WITHOUT AN APP STORE REVIEW — AND EVERY REASON THIS FILE REFUSES TO.
 *
 * The channel itself is four calls into `app/platform/updates.ts`. Everything else here is
 * the guard, because an over-the-air channel is a SECOND PATH BY WHICH A WEB BUNDLE REACHES
 * A DEVICE and the first one is protected by a build phase that this path cannot reach.
 *
 * `ios/scripts/check-env-stamp.sh` refuses to compile when the bundle in `dist/` was built
 * for a different environment than the identity being built. Which service the app talks to,
 * which database it writes to and which push host its token is valid for are ALL decided by
 * the web bundle, so that check is the only thing between "looks completely normal" and
 * "writes to the wrong database". Nothing about an update downloaded at 2am passes through
 * Xcode, so the equivalent refusal has to happen here and in Swift.
 *
 * ## Where each refusal lives, and why they are not all in one place
 *
 * | Refusal | Where | Why there |
 * | --- | --- | --- |
 * | Wrong environment, before a byte is downloaded | this file | cheap, and the error names the actual problem |
 * | Wrong environment, provably | the plugin, in Swift | one signing key per environment, compiled into the SHELL; a staging bundle cannot be unpacked by a production binary |
 * | Wrong environment, once it is running | `markBundleHealthy` below | the last backstop: reverts and never marks healthy |
 * | Never booted | the plugin's rollback timer | no JavaScript runs, so no JavaScript can decide |
 *
 * The middle row is the one that actually holds. The check in this file compares a CLAIM in
 * a document fetched over the network against the shell's identity — useful, and worth doing
 * first because it produces a message somebody can act on, but a document is only as
 * trustworthy as whoever is serving it. The signature is not: the key that verifies it is
 * compiled into the binary, one per environment, and an update cannot change it.
 *
 * ## The kill switch is consumed here, and "unreachable" counts as off
 *
 * `app/config/manifest.ts` carries `ota.enabled` and defaults it to false, with the reasoning
 * written there: *a channel that turns itself on when the kill switch is unreachable is not
 * one*. This file goes one step further and requires the manifest to have come FROM THE
 * NETWORK this session. A cached "yes" from yesterday morning is precisely the case that
 * reasoning is about — the phone has not heard from the service, so it does not know whether
 * the switch has since been thrown.
 */

/**
 * WHAT THE CHANNEL PUBLISHES — a small document at a URL compiled into this build.
 *
 * It is UNTRUSTED INPUT and is treated as such: every field is a claim, none of them is the
 * thing that makes the update safe, and the two that decide anything (`env` and `version`)
 * are checked again by machinery an update cannot reach. Parse rather than cast, so a
 * captive portal's login page or a truncated body ends as `null` instead of as `undefined`
 * reaching a bridge call.
 */
export const otaIndexSchema = z.object({
  /**
   * Which build this bundle was made for. Written by `scripts/release/ota-publish.mjs` from
   * `dist/.env-stamp` — the same file the Xcode build phase reads — and never typed by hand.
   */
  env: z.enum(["dev", "staging", "production"]),
  /** Semver, and also the bundle's identifier on the device. */
  version: z.string().min(1),
  /** Where the archive lives. Any static host; it never touches the service. */
  url: z.url(),
  /** Base64 RSA PKCS#1 v1.5 over the SHA-256 digest of the archive. */
  signature: z.string().min(1),
  /** SHA-256 of the archive, hex. */
  checksum: z.string().min(1),
  /**
   * THE OLDEST BINARY THIS BUNDLE MAY RUN INSIDE — the additive-migration rule, pointed at
   * the JavaScript-to-native bridge.
   *
   * An updated bundle can be NEWER than the shell it runs in; someone is always carrying a
   * months-old binary. So a bundle that needs a plugin, a permission string or an
   * entitlement the old shell does not have must say so, and this is where it says it.
   * `0.0.0` means "nothing new is needed", which is what a bug fix should almost always say.
   */
  minimumShellVersion: z.string().min(1),
});

export type OtaIndex = z.infer<typeof otaIndexSchema>;

/**
 * EVERY WAY THIS CAN END, NAMED. Returned rather than thrown, because none of these is a
 * failure of the app — the correct outcome of an update check is usually "no".
 */
export type OtaOutcome =
  /** A browser, or a binary built before the update plugin existed. */
  | "unsupported"
  /** A check was already in flight; this one did nothing rather than racing it. */
  | "already-checking"
  /** This build has no channel compiled in, which is how a build opts out entirely. */
  | "not-configured"
  /** The kill switch is off, or the service has not been reached this session. */
  | "disabled"
  /** The channel could not be read. Not reported: a phone on a train produces hundreds. */
  | "unreachable"
  /** Something answered, and it was not a document this build understands. */
  | "unreadable"
  /** 🚨 The channel is offering a bundle built for a DIFFERENT environment. Reported. */
  | "wrong-environment"
  /** Below `ota.minimumBundleVersion` — the service has withdrawn this bundle. */
  | "below-floor"
  /** Not newer than what is already running. */
  | "not-newer"
  /** The bundle needs native capabilities this binary does not have. */
  | "shell-too-old"
  /** 🚨 The plugin refused it — signature or checksum. Reported. */
  | "refused"
  /** Downloaded and verified, but could not be armed for the next launch. */
  | "not-staged"
  /** Armed. It takes effect on the next cold launch, never mid-session. */
  | "staged";

/** How long the index fetch may take. A phone on a bad connection is not worth waiting for. */
const OTA_FETCH_TIMEOUT_MS = 8_000;

/**
 * WHICH BUILD THE SHELL IS, FROM ITS BUNDLE IDENTIFIER.
 *
 * The three identities are set in `ios/config/{dev,staging,production}.xcconfig` and differ
 * only by suffix — `<base>.dev`, `<base>.staging`, `<base>`. Reading the suffix rather than
 * a compiled-in constant is the entire point: `VITE_APP_ENV` lives in the WEB BUNDLE, so a
 * bundle comparing it against itself always agrees with itself. The identifier comes from
 * the shell's `Info.plist`, which nothing shipped over the air can touch.
 */
export function shellEnvironment(appId: string): Exclude<AppEnv, "test"> {
  if (appId.endsWith(".dev")) return "dev";
  if (appId.endsWith(".staging")) return "staging";
  return "production";
}

/**
 * "THIS BUNDLE BOOTED, AND IT IS THE RIGHT BUNDLE FOR THIS PHONE."
 *
 * Called once, as early in start-up as anything runs, and NEVER behind something that can
 * hang. The native side arms a rollback timer when the plugin loads (`readyTimeout` in
 * `capacitor.config.ts`); marking healthy cancels it. Putting a network call, a reporting
 * SDK's dynamic import, or a session restore in front of this would let a perfectly good
 * bundle be rolled back for being slow.
 *
 * THE ORDER MATTERS AND IS THE OPPOSITE OF WHAT LOOKS NATURAL. The safety checks run BEFORE
 * `ready()`, because `ready()` is what disarms the rollback. A bundle that fails them is
 * reverted to the copy compiled into the binary and never marked healthy at all — the app
 * reloads, start-up runs again from the built-in bundle, and that one passes, so there is no
 * loop.
 *
 * ONE CHECK IS DELIBERATELY AFTER `ready()`, AND ONLY ONE. The withdrawal floor needs the
 * cached manifest, and reading it is a trip to the preference store — small, but not nothing,
 * and the whole point of this function is that a slow launch cannot cost a good bundle its
 * rollback timer. A withdrawn bundle is not a bundle that fails to BOOT, so reverting it a
 * few hundred milliseconds later costs nothing; the revert works just as well once the timer
 * is cancelled. Putting that read in front of `ready()` would trade a real safety property
 * for a cosmetic ordering.
 */
export async function markBundleHealthy(): Promise<void> {
  if (!updates.isSupported()) return;

  const [shell, runningBundleId] = await Promise.all([
    updates.shellIdentity(),
    updates.currentBundleId(),
  ]);

  /**
   * `null` means the built-in bundle — the one Xcode compiled in, whose environment the
   * build phase already proved, and which no check here applies to. The floor in particular:
   * applied to the built-in bundle a raised floor would wall off the one build nobody can
   * replace without an App Store release, which is the exact failure this feature exists to
   * avoid.
   */
  if (runningBundleId !== null) {
    const problem = bootProblem(shell, runningBundleId);
    if (problem) {
      /**
       * REPORTED BEFORE THE REVERT, and the order is load-bearing rather than stylistic.
       * Reverting reloads the web view onto the built-in bundle, which tears down this
       * JavaScript context — a report queued after that call is a report that never leaves
       * the device, and this is the one event worth hearing about, because on the phone it
       * looks like nothing happened at all.
       */
      reportMessage("over-the-air bundle refused after boot; reverting to the built-in bundle", {
        problem,
        runningBundleId,
        bundleEnv: env.appEnv,
        bundleVersion: env.version,
      });
      await updates.revertToBuiltIn();
      return;
    }
  }

  const health = await updates.markHealthy();

  /**
   * The previous launch never got this far. Worth an issue rather than a log line: it means
   * a published bundle threw before React mounted, which is the failure the rollback exists
   * for and the one nobody would otherwise hear about — the user just sees the app they had.
   */
  if (health?.rolledBack) {
    reportMessage("an over-the-air bundle failed to boot and was rolled back", {
      bundleVersion: env.version,
    });
  }

  if (runningBundleId !== null) await enforceBundleFloor(runningBundleId);
}

/**
 * THE WITHDRAWAL FLOOR, READ FROM AN ANSWER THAT HAS ACTUALLY BEEN LOADED.
 *
 * `getManifest()` never blocks and never fails, which means it happily answers with the
 * PERMISSIVE DEFAULTS — floor `0.0.0` — while the cached copy is still being read off the
 * device. Start-up fires that read in the same tick as this function, so reading the floor
 * without waiting is a coin toss: a bundle the service has withdrawn would survive a launch
 * or not depending on which local read won, and `docs/ota.md` promises it does not.
 *
 * `primeManifestFromCache()` is memoised, so this shares the read start-up already began
 * rather than making a second one.
 */
async function enforceBundleFloor(runningBundleId: string): Promise<void> {
  await primeManifestFromCache();

  const floor = getManifest().ota.minimumBundleVersion;
  if (compareVersions(env.version, floor) >= 0) return;

  reportMessage("over-the-air bundle refused after boot; reverting to the built-in bundle", {
    problem: "below-floor",
    runningBundleId,
    bundleEnv: env.appEnv,
    bundleVersion: env.version,
  });
  await updates.revertToBuiltIn();
}

/**
 * The four ways a bundle can be wrong for the phone it woke up on before anything has been
 * read off the device. Split out so the test suite can hold each one separately, and so the
 * report says which.
 */
function bootProblem(shell: ShellIdentity | null, runningBundleId: string): string | null {
  /**
   * FAIL CLOSED. Everything below needs to know what the binary is, so an answer of "I could
   * not find out" cannot mean "carry on" — that would be the one path in this file where an
   * unverified bundle gets its rollback timer cancelled. Reverting to the built-in bundle is
   * never dangerous (it is the one the App Store reviewed), and the channel simply re-arms
   * the update on the next check, so the cost of being wrong here is a relaunch.
   */
  if (!shell) return "unknown-shell";

  if (shellEnvironment(shell.appId) !== env.appEnv) return "environment-mismatch";

  /**
   * THE APP STORE UPDATE THAT LOOKS LIKE IT DID NOTHING — and the plugin does not handle it.
   *
   * The active bundle is a path in `UserDefaults` and the bundle files sit in the app's
   * container; both survive an App Store update, and the plugin only clears its cloud
   * config when the binary's version changes, not the bundle it is pointing at (read
   * `checkAndResetConfigIfVersionChanged` in its `LiveUpdate.swift`). So someone running an
   * over-the-air 1.4.3 who installs store build 1.5.0 boots 1.4.3 again: they updated and
   * got nothing, the fixes in 1.5.0's JavaScript are not there, and the version floor judges
   * them on 1.4.3 and may well tell them to update — which they just did.
   *
   * A binary is never older than the bundle it shipped with, so "the shell is newer than
   * what is running" can only mean a store update has overtaken this bundle. The built-in
   * copy is then the newest JavaScript on the device, by definition.
   */
  if (compareVersions(env.version, shell.version) < 0) return "overtaken-by-the-store";

  /**
   * The plugin's bookkeeping and the running code disagree about what is running. Nothing
   * should be able to produce this — the identifier IS the version at publish time — so it
   * means a bundle was armed under a name that is not its own, and the safe reading of "I do
   * not know what is running" is to stop running it.
   */
  if (runningBundleId !== env.version) return "identity-mismatch";

  /**
   * The withdrawal floor is NOT here. It needs a value read off the device, and everything in
   * this function has to answer without waiting for one — see `enforceBundleFloor`.
   */
  return null;
}

/**
 * ONE CHECK, AND EVERY FAILURE LEAVES THE APP EXACTLY AS IT WAS.
 *
 * Called from the manifest watcher in `app/providers/AppProviders.tsx`, which fires after
 * every attempt to reach the service — successful or not. That is deliberately the only
 * trigger: this must not run on a cached answer, and "the service has just answered" is the
 * one moment when it is known not to be.
 */
export async function checkForUpdate(): Promise<OtaOutcome> {
  /**
   * ONE AT A TIME, AND THIS IS A LATCH RATHER THAN A THROTTLE. The manifest's own clock
   * already decides how OFTEN this is worth asking; what this stops is two checks running at
   * the same time, which would download the same archive twice and unpack both into the same
   * bundle id. There is one caller today and it cannot overlap itself — this is here so that
   * a second caller added later cannot be the way that is discovered.
   */
  if (checking) return "already-checking";
  checking = true;
  try {
    return await runCheck();
  } finally {
    checking = false;
  }
}

let checking = false;

async function runCheck(): Promise<OtaOutcome> {
  if (!updates.isSupported()) return "unsupported";
  if (!env.otaUpdateUrl) return "not-configured";

  const manifest = getManifest();
  if (!manifest.ota.enabled) return "disabled";
  /**
   * A CACHED YES IS A NO. `stale` means nothing has been heard from the service this
   * session, so the kill switch is unreachable — and a channel that keeps running on
   * yesterday's permission is not one that can be switched off.
   */
  if (getManifestStatus().stale) return "disabled";

  const shell = await updates.shellIdentity();
  if (!shell) return "unsupported";

  const index = await fetchIndex(env.otaUpdateUrl);
  if (index === "unreachable") return "unreachable";
  if (index === null) return "unreadable";

  /**
   * 🚨 THE REFUSAL THIS WHOLE FILE IS FOR, and it is checked against the SHELL rather than
   * against this bundle's own compiled-in environment, which would compare a value to
   * itself. It is reported because it is never normal: a staging bundle offered to a
   * production install means the publish went to the wrong place, and the next thing to
   * happen would have been an app that looks right and writes to the wrong database.
   */
  if (index.env !== shellEnvironment(shell.appId)) {
    reportMessage("over-the-air channel offered a bundle for another environment", {
      offered: index.env,
      shell: shell.appId,
      offeredVersion: index.version,
    });
    return "wrong-environment";
  }

  if (compareVersions(index.version, manifest.ota.minimumBundleVersion) < 0) return "below-floor";
  if (compareVersions(index.version, env.version) <= 0) return "not-newer";
  /**
   * The bundle says which binary it needs. Comparing against the SHELL's version, not this
   * bundle's: an app updated over the air three times still runs inside the binary the store
   * installed, and that is what has the plugins in it.
   */
  if (compareVersions(shell.version, index.minimumShellVersion) < 0) return "shell-too-old";

  const downloaded = await updates.download({
    bundleId: index.version,
    url: index.url,
    signature: index.signature,
    checksum: index.checksum,
  });

  /**
   * 🚨 The signature did not verify against the key compiled into this binary, or the bytes
   * did not hash to what the index named. Either is somebody serving something they should
   * not be, or a publish signed with the wrong environment's key. Reported; nothing was
   * unpacked and nothing was armed.
   */
  if (!downloaded) {
    reportMessage("over-the-air bundle refused by the native verifier", {
      offeredVersion: index.version,
      url: index.url,
    });
    return "refused";
  }

  const staged = await updates.stage(index.version);
  if (!staged) return "not-staged";

  log.info("over-the-air bundle armed for the next launch", { version: index.version });
  return "staged";
}

/**
 * `"unreachable"` and `null` are different answers on purpose: one is a phone in a tunnel
 * and is not a defect, the other is something answering with a document this build cannot
 * read, which usually means the publish is broken and somebody should look.
 *
 * NOT `apiRequest`. This is a static file on whatever host the bundle lives on — it is not
 * the service, it carries no session, and routing it through the transport would attach a
 * bearer token to a request going somewhere that has no business seeing one.
 */
async function fetchIndex(url: string): Promise<OtaIndex | "unreachable" | null> {
  const response = await fetch(url, {
    cache: "no-store",
    signal: AbortSignal.timeout(OTA_FETCH_TIMEOUT_MS),
  }).catch(() => null);

  if (!response || !response.ok) return "unreachable";

  const body: unknown = await response.json().catch(() => null);
  const parsed = otaIndexSchema.safeParse(body);
  return parsed.success ? parsed.data : null;
}
